// @ts-strict-ignore
import {AfterViewInit, Component, ContentChild, Input, OnInit, TemplateRef, output} from "@angular/core";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {
	animate,
	animateChild,
	animation,
	group,
	keyframes,
	query,
	state,
	style,
	transition,
	trigger,
	useAnimation,
} from "@angular/animations";
import {IAnimationOptions} from "angular-animations/common/interfaces";
import {SimUtilityExtendedService} from "@services";
import {Translations} from "@translations";
import {FormControl} from "@angular/forms";

const zoomIn = () =>
	animation(
		group([
			animate(
				"{{duration}}ms {{delay}}ms",
				keyframes([
					style({opacity: 0, easing: "ease", offset: 0}),
					style({opacity: 1, easing: "ease", offset: 0.5}),
					style({opacity: 1, easing: "ease", offset: 1}),
				]),
			),
			animate(
				"{{duration}}ms {{delay}}ms",
				keyframes([
					style({
						visibility: "visible",
						transform: "scale3d(0.3, 0.3, 0.3)",
						easing: "ease",
						offset: 0,
					}),
					style({
						transform: "scale3d(1, 1, 1)",
						easing: "ease",
						offset: 1,
					}),
				]),
			),
		]),
	);

export function useAnimationIncludingChildrenCustom(animation, options) {
	return [
		...(options && options.animateChildren === "before" ? [query("@*", animateChild(), {optional: true})] : []),
		group([
			useAnimation(animation),
			...(!options || !options.animateChildren || options.animateChildren === "together"
				? [query("@*", animateChild(), {optional: true})]
				: []),
		]),
		...(options && options.animateChildren === "after" ? [query("@*", animateChild(), {optional: true})] : []),
	];
}

const zoomInOptions: IAnimationOptions = {
	duration: 175,
	delay: 0,
};

@Component({
	selector: "sim-admin-settings-input-to-label-transformer",
	templateUrl: "./sim-admin-settings-input-to-label-transformer.component.html",
	styleUrls: ["./sim-admin-settings-input-to-label-transformer.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175}),
		zoomOutOnLeaveAnimation({duration: 175}),
		trigger("flyInOut", [
			state("in", style({transform: "translateX(0)"})),
			transition("void => *", [style({visibility: "hidden"}), ...useAnimationIncludingChildrenCustom(zoomIn(), zoomInOptions)], {
				params: {
					delay: (zoomInOptions && zoomInOptions.delay) || 0,
					duration: zoomInOptions && zoomInOptions.duration,
				},
			}),
			transition("* => void", [animate(0, style({transform: "translateX(100%)"}))]),
		]),
	],
	standalone: false,
})
export class SimAdminSettingsInputToLabelTransformerComponent<T extends number | string> implements OnInit, AfterViewInit {
	T = Translations;

	@Input() wrapText: boolean = false;
	@Input() inEditMode: boolean = false;
	@Input() labelText: string;
	@Input() value: T;
	@Input() showCopyBtn: boolean = true;
	@Input() textCentered: boolean = false;
	@Input() overflowAfterLines: 1 | 2 | 3 | 4 | 5 = 1;
	@Input() required: boolean = false;
	@ContentChild(TemplateRef) templateRef: TemplateRef<any>;

	valueHasChanged = output<T>();
	enterPressedEmitter = output<void>();

	canAnimate = false;
	@Input() type: "text" | "number" | "password" = "text";
	@Input() step: number = 1;

	private _valueUpdated: boolean = false;
	private _fc: FormControl<T> = new FormControl<T>(undefined);

	constructor(public _simUtilityService: SimUtilityExtendedService) {}

	ngOnInit(): void {}

	ngAfterViewInit(): void {
		this.canAnimate = true;
	}

	hashPassword(value: T) {
		return "*".repeat(String(value).length);
	}

	onModelChange(value: T) {
		this.value = value;
		this._valueUpdated = true;
		this.valueHasChanged.emit(value);
	}

	isValueUpdated(): boolean {
		return this._valueUpdated;
	}

	copyValue(value: T) {
		this._simUtilityService.copyToClipBoard(String(value));
	}
}
